'use client'

import type { ActionResult } from '@mentora/app-shared/i18n/action-result'
import { useActionToast } from '@mentora/app-shared/i18n/use-action-toast'
import { Button, Input, Label } from '@mentora/ui-components'
import { useState, useTransition } from 'react'

export interface PercentRateFormCopy {
  title: string
  subtitle: string
  label: string
  suffix: string
  hint: string
  submit: string
  submitting: string
  invalid: string
  saved: string
}

export interface PercentRateFormProps {
  id: string
  /** The stored value, in basis points (1bp = 0.01%). */
  rateBps: number
  /** Inclusive upper bound, in basis points; 10 000 is 100%. */
  maxBps: number
  copy: PercentRateFormCopy
  save: (input: { rateBps: number }) => Promise<ActionResult>
}

function bpsToPercentInput(bps: number): string {
  if (!bps)
    return '0'
  return (bps / 100).toString().replace('.', ',')
}

function parsePercentToBps(value: string, maxBps: number): number | null {
  const normalized = value.trim().replace(',', '.')
  if (!normalized)
    return null
  const num = Number.parseFloat(normalized)
  if (!Number.isFinite(num) || num < 0)
    return null
  const bps = Math.round(num * 100)
  return bps > maxBps ? null : bps
}

/** One percentage setting stored as basis points: input, validation, save and toast. */
export function PercentRateForm({ id, rateBps, maxBps, copy, save }: PercentRateFormProps) {
  const { showError, showSuccess } = useActionToast()
  const [isPending, startTransition] = useTransition()
  const [input, setInput] = useState(bpsToPercentInput(rateBps))
  const [error, setError] = useState<string | null>(null)

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    const bps = parsePercentToBps(input, maxBps)
    if (bps === null) {
      setError(copy.invalid)
      return
    }
    startTransition(async () => {
      const result = await save({ rateBps: bps })
      if (!result.ok) {
        showError(result)
        return
      }
      showSuccess(copy.saved)
    })
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4 rounded-lg border border-border/60 p-4">
      <div>
        <p className="text-sm font-medium text-foreground">{copy.title}</p>
        <p className="text-sm text-muted-foreground">{copy.subtitle}</p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={id}>{copy.label}</Label>
        <div className="flex items-center gap-2">
          <Input
            id={id}
            type="text"
            inputMode="decimal"
            className="max-w-[160px]"
            value={input}
            onChange={(e) => {
              setInput(e.target.value)
              setError(null)
            }}
            placeholder="0"
          />
          <span className="text-sm text-muted-foreground">{copy.suffix}</span>
        </div>
        <p className="text-xs text-muted-foreground">{copy.hint}</p>
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
      <div className="flex justify-end">
        <Button type="submit" disabled={isPending}>
          {isPending ? copy.submitting : copy.submit}
        </Button>
      </div>
    </form>
  )
}
